import {
    BotMessageSquare,
    Database,
    Folder,
    Folders,
    LayoutGrid,
    LibraryBig,
    MessageSquare,
    Palette
} from 'lucide-vue-next';
import {
    createRouter,
    createWebHistory,
    type RouteRecordRaw
} from 'vue-router';

import accessContextGuard from '@/guards/accessContextGuard';
import authGuard from '@/guards/authGuard';
import microsoftConsentGuard from '@/guards/microsoftConsentGuard';
import redirectRootGuard from '@/guards/redirectRootGuard';
import requiredPermissionGuard from '@/guards/requiredPermissionGuard';
import subscriptionAndOnboardingGuard from '@/guards/subscriptionAndOnboardingGuard';

const routes: RouteRecordRaw[] = [
    {
        path: '/chat/:session_id',
        component: () => import('@/views/ChatView.vue'),
        name: 'Chat',
        meta: {
            icon: MessageSquare,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/chat',
        component: () => import('@/views/WelcomeChatView.vue'),
        name: 'Welcome',
        meta: {
            icon: MessageSquare,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/data-sources',
        component: () => import('@/views/DataSourcesView.vue'),
        name: 'Documents',
        meta: {
            icon: Folder,
            showInSidebar: true,
            showInUserMenu: false
        }
    },
    {
        path: '/data-sources/:id',
        component: () => import('@/views/DataSourceView.vue'),
        name: 'DataSource',
        meta: {
            icon: Database,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/catalog-management',
        component: () => import('@/views/CatalogManagementView.vue'),
        name: 'Catalog management',
        meta: {
            icon: LibraryBig,
            requiredPermission: 'admin',
            showInSidebar: true,
            showInUserMenu: false
        }
    },
    {
        path: '/theme-management',
        component: () => import('@/views/SlideStudioThemeManagementView.vue'),
        name: 'Themes',
        meta: {
            icon: Palette,
            showInSidebar: true,
            showInUserMenu: false
        }
    },
    {
        path: '/custom-agents',
        component: () => import('@/views/CustomAgentsView.vue'),
        name: 'Custom Agents',
        meta: {
            icon: BotMessageSquare,
            showInSidebar: true,
            showInUserMenu: false
        }
    },
    {
        path: '/my-library',
        component: () => import('@/views/MyLibraryView.vue'),
        name: 'Library',
        meta: {
            icon: LayoutGrid,
            showInSidebar: true,
            showInUserMenu: false
        }
    },
    {
        path: '/projects',
        component: () => import('@/views/ProjectsView.vue'),
        name: 'Projects',
        meta: {
            icon: Folders,
            showInSidebar: true,
            showInUserMenu: false
        }
    },
    {
        path: '/projects/:id',
        component: () => import('@/views/ProjectView.vue'),
        name: 'Project',
        meta: {
            icon: Folders,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/start-free-trial',
        component: () => import('@/views/StartFreeTrialView.vue'),
        name: 'StartFreeTrial',
        meta: {
            allowWithoutOnboarding: true,
            allowWithoutSubscription: true,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/subscription-required',
        component: () => import('@/views/SubscriptionRequiredView.vue'),
        name: 'SubscriptionRequired',
        meta: {
            allowWithoutSubscription: true,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/onboarding',
        component: () => import('@/views/OnboardingView.vue'),
        name: 'Onboarding',
        meta: {
            allowWithoutOnboarding: true,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/unauthorized',
        component: () => import('@/views/UnauthorizedView.vue'),
        name: 'Unauthorized',
        meta: {
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/viewer',
        component: () => import('@/views/ViewerLayout.vue'),
        name: 'Unified Viewer',
        props: (route) => ({
            chatId: route.query.chatId,
            fileId: route.query.fileId,
            fileType: route.query.fileType
        }),
        meta: {
            icon: MessageSquare,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/presentation',
        component: () => import('@/views/WelcomePresentationView.vue'),
        name: 'New Presentation',
        meta: {
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/login',
        component: () => import('@/views/LoginView.vue'),
        name: 'Login',
        meta: {
            public: true,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/sign-up',
        component: () => import('@/views/SignUpView.vue'),
        name: 'SignUp',
        props: { locale: 'en' },
        meta: {
            public: true,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/sign-up/fr',
        component: () => import('@/views/SignUpView.vue'),
        name: 'SignUpFr',
        props: { locale: 'fr' },
        meta: {
            public: true,
            showInSidebar: false,
            showInUserMenu: false
        }
    },
    {
        path: '/:pathMatch(.*)*',
        component: () => import('@/views/NotFoundView.vue'),
        name: 'NotFound',
        meta: {
            showInSidebar: false,
            showInUserMenu: false
        }
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

router.beforeEach(authGuard);
router.beforeEach(accessContextGuard);
router.beforeEach(subscriptionAndOnboardingGuard);
router.beforeEach(requiredPermissionGuard);
router.beforeEach(redirectRootGuard);
// Last: `redirectRootGuard` carries the query over to /chat and its redirect re-runs
// the chain, so this sees Microsoft's parameters on the path they end up on.
router.beforeEach(microsoftConsentGuard);

export { routes };
export default router;
